import styles from './CancellationDone.module.scss';
import { Icon } from 'src/components/Icon';
import { TextDivider } from 'src/v2/commonComponents/TextDivider';
import { Button } from 'src/v2/commonComponents/Button';
import { useNavigate } from 'react-router-dom';
import { useColorTheme, useGetUser, useSubscriptionInfo } from 'src/hooks';
import dayjs from 'dayjs';
import { PAYMENTS_DATE_FORMAT } from 'src/constants';
import { toast } from 'react-toastify';
import log from 'src/utils/logger';
import { useResumeSubscriptionMutation } from 'src/store/services';
import { Modal } from 'react-responsive-modal';
import { convertTierName } from 'src/utils/user';

type CancellationDoneProps = {
  cancellationDate?: string;
};

export const CancellationDone = ({
  cancellationDate,
}: CancellationDoneProps) => {
  const { user_id, tier_id } = useGetUser();
  const { isDarkTheme } = useColorTheme();
  const { subscriptionInfo } = useSubscriptionInfo();
  const navigate = useNavigate();

  const [resumeSubscription, { isLoading: isResumeSubscriptionLoading }] =
    useResumeSubscriptionMutation();

  const lastDate = dayjs(
    cancellationDate || subscriptionInfo?.curr_period_end_timestamp,
  ).format(PAYMENTS_DATE_FORMAT);

  const handleRestoreSubscription = async () => {
    if (!subscriptionInfo || !subscriptionInfo?.price_info?.lookup_key) {
      return;
    }

    try {
      await resumeSubscription(user_id).unwrap();
      toast.success('Subscription renewed successfully.');
    } catch (error) {
      toast.error('Failed to renew subscription.');
      log.error(error);
    } finally {
      navigate(-1);
    }
  };

  const handleDoneClick = () => {
    navigate(-1);
    toast.success('Subscription cancelled');
  };

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.header}>
          <Icon
            type={isDarkTheme ? 'cancelIconDark' : 'cancelIconLight'}
            width={96}
            height={102}
          />
          <span className={styles.title}>Subscription has been canceled.</span>
        </div>
        <div className={styles.content}>
          {cancellationDate ? (
            <span className={styles.text}>
              You’ll no longer be charged! We are continuously improving our
              product and are providing you with <b>complimentary access</b> to
              Ninja until {lastDate}.
            </span>
          ) : (
            <span className={styles.text}>
              You’ll no longer be charged! You will have access to Ninja{' '}
              <span className={styles.noWrapText}>until {lastDate}.</span>
            </span>
          )}
        </div>
        <TextDivider />
        <div className={styles.footer}>
          <div className={styles.restoreSubscriptionRow}>
            <span className={styles.text}>Changed your mind?</span>
            <Button
              color="link"
              onClick={handleRestoreSubscription}
              disabled={isResumeSubscriptionLoading}
            >
              Restore your subscription
            </Button>
          </div>
          <Button
            color="secondary-outline"
            onClick={handleDoneClick}
            disabled={isResumeSubscriptionLoading}
          >
            Done
          </Button>
        </div>
      </div>
      <Modal
        open={isResumeSubscriptionLoading}
        onClose={
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          () => {}
        }
        container={document.body}
        classNames={{
          root: 'nj-modal--root',
          overlay: 'nj-modal--overlay',
          modal: styles.modal,
        }}
        center
        closeOnOverlayClick={false}
        showCloseIcon={false}
      >
        <div className={styles.modalContent}>
          <Icon type="ninjaMainIcon" size={96} />
          <h4 className={styles.modalTitle}>
            Restoring your {convertTierName(tier_id || '')} subscription
          </h4>
          <span className={styles.description}>
            This may take a few minutes...
          </span>
        </div>
      </Modal>
    </div>
  );
};
